import React, { useEffect, useState } from "react";
import { useReportContext } from "../../../_context/ReportContext";
import { Report } from "../../../_models/Report.model";
import { ReportViewModal } from "./ReportTemplate/ReportViewModal";
import { useClientContext } from "../../../_context/ClientContext";
import { useUserContext } from "../../../_context/UserContext";
import { useNavigate } from "react-router-dom";
import { ReportsTable } from "../../../htmlComponents/ReportsTable";
import { getPaginatedReports, deleteReport } from '../../../_utils/resourceAPI';
import { Modal, Button } from "react-bootstrap";
import "../../Admin/AddUsers/AddUsers.css";
import "../Reports.css";

const LoadingSpinner = () => (
  <div className="rp-spinner-wrap" style={{ padding: "80px 20px" }}>
    <svg className="rp-spinner" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#2563C0" strokeWidth="2.5" strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
    <span>Loading Client Reports...</span>
  </div>
);

export const ClientReports = () => {
  const {
    filteredInspectionReportsForSelectedClient,
    filteredInternalReportsForSelectedClient,
    setCurrentViewingReport,
    setFilteredInternalReportsForSelectedClient,
    setFilteredInspectionReportsForSelectedClient,
    setNewReport
  } = useReportContext();

  const [viewReport, setViewReport] = useState<boolean>(false);
  const { client } = useClientContext();
  const [activeTab, setActiveTab] = useState<"inspection" | "internal">("inspection");

  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { userName, isUserAdmin } = useUserContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (client && client.Id) {
      setIsLoading(true);
      getPaginatedReports(limit, page * limit, client.Id)
        .then((res) => {
          const allFetchedReports: Report[] = res.data.reports || [];
          setTotal(res.data.total_count || 0);

          const inspectionReports = allFetchedReports.filter((r) => !r.IsInternalReport);
          const internalReports = allFetchedReports.filter((r) => r.IsInternalReport);

          setFilteredInternalReportsForSelectedClient(internalReports);
          setFilteredInspectionReportsForSelectedClient(inspectionReports);
        })
        .catch((error) => console.log('Failed to fetch reports', error))
        .finally(() => setIsLoading(false));
    }
  }, [client, page]);

  const viewReportHandler = (report: Report) => {
    setCurrentViewingReport(report);
    setViewReport(true);
  };

  const duplicateReportHandler = (report: Report) => {
    const duplicatedReport = JSON.parse(JSON.stringify(report)) as Report;
    duplicatedReport.Id = ""; // ensure it creates a new record
    setNewReport(duplicatedReport);
    // Navigate to the first step so the user can change the Date & select Inspector/Internal
    navigate("/reports/create_new_report");
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);

  const handleDeleteClick = (report: Report) => {
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const confirmDeleteHandler = async () => {
    if (!reportToDelete) return;
    try {
      await deleteReport(reportToDelete.Id);
      // Remove from arrays
      setFilteredInspectionReportsForSelectedClient(prev => prev.filter(r => r.Id !== reportToDelete.Id));
      setFilteredInternalReportsForSelectedClient(prev => prev.filter(r => r.Id !== reportToDelete.Id));
      setShowDeleteModal(false);
      setReportToDelete(null);
    } catch (error) {
      console.error("Failed to delete report v1:", error);
      alert("Failed to delete the report. Please try again.");
    }
  };

  const canDuplicate = userName.toLowerCase().includes("ashan");

  const totalPages = Math.ceil(total / limit) || 1;

  if (!client || !client.Id) return null;

  return (
    <div className="reports-page-wrapper">

      {/* Premium Client Header Card */}
      <div className="rp-client-header">
        <h2 className="rp-client-title">{client.Name}</h2>
        <div className="rp-client-email">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          {client.Email}
        </div>
      </div>

      <div className="au-card">
        <div className="au-card-body" style={{ padding: "24px" }}>

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              {/* Pagination Controls */}
              <div className="rp-pagination">
                <span className="rp-pagination-info">
                  Page {page + 1} of {totalPages}
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    className="rp-pagination-btn"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Previous
                  </button>
                  <button
                    className="rp-pagination-btn"
                    onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                    disabled={page + 1 >= totalPages}
                  >
                    Next
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Pill Tabs */}
              <div className="au-tabs" style={{ marginBottom: "0" }}>
                <button
                  className={`au-tab${activeTab === "inspection" ? " active" : ""}`}
                  onClick={() => setActiveTab("inspection")}
                >
                  Inspection Reports ({filteredInspectionReportsForSelectedClient.length})
                </button>
                <button
                  className={`au-tab${activeTab === "internal" ? " active" : ""}`}
                  onClick={() => setActiveTab("internal")}
                >
                  Internal Reports ({filteredInternalReportsForSelectedClient.length})
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === "inspection" ? (
                <ReportsTable
                  reports={filteredInspectionReportsForSelectedClient}
                  noResultsMessage="There are no inspection reports under this client"
                  viewReportHandler={viewReportHandler}
                  duplicateReportHandler={duplicateReportHandler}
                  deleteReportHandler={handleDeleteClick}
                  canDuplicate={canDuplicate}
                  canDelete={isUserAdmin}
                />
              ) : (
                <ReportsTable
                  reports={filteredInternalReportsForSelectedClient}
                  noResultsMessage="There are no internal reports under this client"
                  viewReportHandler={viewReportHandler}
                  duplicateReportHandler={duplicateReportHandler}
                  deleteReportHandler={handleDeleteClick}
                  canDuplicate={canDuplicate}
                  canDelete={isUserAdmin}
                />
              )}
            </>
          )}

        </div>
      </div>

      <ReportViewModal viewReport={viewReport} setViewReport={setViewReport} />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this report? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteHandler}>
            Delete Report
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};
